import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a product variant SKU and keeps it in sync
 * with the currently selected variant.
 *
 * Mirrors the variant-update pattern used by <product-price> and
 * <product-inventory>: it listens for `variant:update` on the closest section
 * (or dialog), then pulls the freshly rendered SKU from the section HTML
 * delivered with the event.
 *
 * When the product uses `custom.variant_option_behavior.unselected_by_position`
 * to force an active variant choice, the SKU stays hidden until the shopper
 * has satisfied the validation (all required options picked). This avoids
 * showing an SKU for a variant the customer hasn't actually selected.
 */
class ProductSku extends HTMLElement {
  /** @type {Element | null} */
  #section = null;

  /** @type {boolean} */
  #hasUnselectedOptions = false;

  /**
   * Tracks whether all required variant options are currently selected.
   * Set in `connectedCallback` to `!#hasUnselectedOptions`, so products
   * without any unselected-options config default to valid, while products
   * with the config start invalid and only flip true once the variant-picker
   * emits a valid `variant:validation` event.
   */
  #isValid = true;

  /** Tracks whether the latest server render considers the SKU blank. */
  #serverEmpty = false;

  connectedCallback() {
    this.#syncUnselectedOptionsFlag();
    const skuText = this.querySelector('[ref="skuValue"]')?.textContent?.trim() ?? '';
    this.#serverEmpty = skuText === '';

    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.updateSku);
    this.#section?.addEventListener(ThemeEvents.variantValidation, this.updateValidation);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.updateSku);
    this.#section?.removeEventListener(ThemeEvents.variantValidation, this.updateValidation);
    this.#section = null;
  }

  /**
   * Reads `data-has-unselected-options` from this element and resets the
   * validity baseline. Call on connect and after any server render that
   * may have replaced the product (e.g. connected-product swaps).
   */
  #syncUnselectedOptionsFlag() {
    this.#hasUnselectedOptions = this.dataset.hasUnselectedOptions === 'true';
    this.#isValid = !this.#hasUnselectedOptions;
  }

  /**
   * Reacts to the variant-picker validation event and toggles visibility.
   * @param {CustomEvent<{ result: { valid: boolean, unselectedPositions: number[] } }>} event
   */
  updateValidation = (event) => {
    const target = event.target;
    if (target instanceof HTMLElement) {
      const pickerProductId = target.dataset.productId;
      if (pickerProductId && pickerProductId !== this.dataset.productId) return;
    }

    this.#isValid = event.detail?.result?.valid ?? true;
    this.#applyVisibility();
  };

  /**
   * Updates the SKU value (and visibility) based on the new variant.
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event.
   */
  updateSku = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const newSku = event.detail.data.html.querySelector('product-sku');
    const currentValue = this.querySelector('[ref="skuValue"]');
    const newValue = newSku?.querySelector('[ref="skuValue"]');

    if (!newSku || !currentValue || !newValue) return;

    if (currentValue.textContent !== newValue.textContent) {
      currentValue.textContent = newValue.textContent;
    }

    // Re-sync the unselected-options baseline whenever a new product is
    // swapped in (connected-product navigation). The new product may have
    // a different `custom.variant_option_behavior` config than the one
    // we originally connected with.
    if (event.detail.data.newProduct && newSku instanceof HTMLElement) {
      const newFlag = newSku.dataset.hasUnselectedOptions === 'true';
      if (newFlag) {
        this.dataset.hasUnselectedOptions = 'true';
      } else {
        delete this.dataset.hasUnselectedOptions;
      }
      this.#syncUnselectedOptionsFlag();
    }

    // Only consider the SKU empty when the server returned blank text.
    // We intentionally ignore `product-meta__sku--empty` on the new node,
    // because the server-rendered Liquid also applies that class whenever
    // the product has `custom.variant_option_behavior.unselected_by_position`
    // set, which would otherwise permanently latch this flag to true.
    const newSkuText = newValue.textContent?.trim() ?? '';
    this.#serverEmpty = newSkuText === '';
    this.#applyVisibility();
  };

  /**
   * Applies the current visibility state. The SKU is hidden when either:
   *  - the server rendered no SKU (blank variant SKU), or
   *  - the product has unselected options and validation is not yet valid.
   */
  #applyVisibility() {
    const shouldHide = this.#serverEmpty || (this.#hasUnselectedOptions && !this.#isValid);
    this.classList.toggle('product-meta__sku--empty', shouldHide);
  }
}

if (!customElements.get('product-sku')) {
  customElements.define('product-sku', ProductSku);
}
